import { useEffect, useState } from 'react';

/**
 * Subscribe to the browser's online/offline events.
 *
 * Returns `true` while the network appears reachable, `false` otherwise.
 * Defaults to `true` when called outside a browser (SSR, jsdom without a
 * navigator) so render code never treats SSR output as offline.
 */
export function useOnline() {
  const [online, setOnline] = useState(() => {
    if (typeof navigator === 'undefined') return true;
    return typeof navigator.onLine === 'boolean' ? navigator.onLine : true;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Reconcile on mount in case the navigator state flipped between the
    // initial state-init and the first effect run.
    if (typeof navigator !== 'undefined' && typeof navigator.onLine === 'boolean') {
      setOnline(navigator.onLine);
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return online;
}

export default useOnline;
